import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { markdownLink } from "@brainlog/shared";
import { get } from "@/lib/api";
import { useTree } from "@/lib/queries";
import type { LibraryItem, Task } from "@/lib/types";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KindIcon } from "./kinds";
import { cn } from "@/lib/utils";
import { formatAuthorsShort } from "@/features/library/format";
import { FileName } from "@/features/files/FileName";
import { useWorkspaceFiles } from "@/features/files/useFiles";

export function InsertLinkDialog(props: { open: boolean; mode: "link" | "cite"; workspaceId: string; onOpenChange(o: boolean): void; onInsert(md: string): void }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<LibraryItem | null>(null);
  const [loc, setLoc] = useState("");
  const [page, setPage] = useState("");
  const tree = useTree(props.workspaceId);
  const library = useQuery({
    queryKey: ["library", props.workspaceId],
    queryFn: () => get<{ items: LibraryItem[] }>(`/api/workspaces/${props.workspaceId}/library`),
  });
  const tasks = useQuery({
    queryKey: ["tasks", props.workspaceId],
    queryFn: () => get<{ tasks: Task[] }>(`/api/tasks?workspaceId=${props.workspaceId}`),
    enabled: props.mode === "link",
  });
  const needle = q.toLowerCase().trim();

  const noteMatches = useMemo(
    () => (tree.data?.notes ?? []).filter((n) => !needle || n.title.toLowerCase().includes(needle)).slice(0, 30),
    [tree.data, needle],
  );
  const libMatches = useMemo(
    () =>
      (library.data?.items ?? [])
        .filter((i) => !needle || `${i.title} ${i.citationKey ?? ""} ${formatAuthorsShort(i.authors)} ${i.year ?? ""}`.toLowerCase().includes(needle))
        .slice(0, 30),
    [library.data, needle],
  );
  const files = useWorkspaceFiles(props.mode === "link" ? props.workspaceId : undefined);
  const fileMatches = useMemo(
    () => (files.data?.files ?? []).filter((f) => !needle || `${f.encrypted ? "" : f.filename} ${f.description ?? ""}`.toLowerCase().includes(needle)).slice(0, 15),
    [files.data, needle],
  );
  const taskMatches = useMemo(() => (tasks.data?.tasks ?? []).filter((t) => !needle || t.title.toLowerCase().includes(needle)).slice(0, 15), [tasks.data, needle]);

  const insertCitation = () => {
    if (!picked) return;
    const key = picked.citationKey ? `@${picked.citationKey}` : `@${picked.id.slice(0, 8)}`;
    const label = loc.trim() ? `${key}, ${loc.trim()}` : key;
    const p = Number(page);
    props.onInsert(markdownLink(label, { type: "cite", id: picked.id, loc: loc.trim() || undefined, page: Number.isInteger(p) && p > 0 ? p : undefined }));
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{props.mode === "cite" ? "Insert citation" : "Insert link"}</DialogTitle>
          <DialogDescription>
            {props.mode === "cite"
              ? "Cite a paper from this project's library; add a page or section if you like."
              : "Links keep working when you rename or move things."}
          </DialogDescription>
        </DialogHeader>
        <Label htmlFor="link-q" className="sr-only">
          Search
        </Label>
        <Input id="link-q" autoFocus placeholder={props.mode === "cite" ? "Search title, author, key…" : "Search notes, records, files and tasks…"} value={q} onChange={(e) => setQ(e.target.value)} />
        {props.mode === "cite" ? (
          <div className="space-y-3">
            <ul className="max-h-64 space-y-1 overflow-y-auto" aria-label="Papers">
              {libMatches.map((i) => (
                <li key={i.id}>
                  <button
                    type="button"
                    onClick={() => setPicked(i)}
                    aria-pressed={picked?.id === i.id}
                    className={cn("w-full rounded-md border px-2 py-1.5 text-left text-sm hover:bg-accent", picked?.id === i.id && "border-primary bg-accent")}
                  >
                    <span className="font-medium">{i.title || "Untitled source"}</span>
                    <span className="block text-xs text-muted-foreground">
                      {formatAuthorsShort(i.authors) || "Authors not recorded"} · {i.year ?? "year not recorded"} · {i.citationKey ? `@${i.citationKey}` : "no citation key yet"}
                    </span>
                  </button>
                </li>
              ))}
              {library.data && !libMatches.length && <li className="text-sm text-muted-foreground">No matching papers.</li>}
            </ul>
            {picked && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="cite-loc">Page or section as printed (optional)</Label>
                  <Input id="cite-loc" placeholder="p. 12, §3, Table 2" value={loc} onChange={(e) => setLoc(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="cite-page">PDF page to open (optional)</Label>
                  <Input id="cite-page" inputMode="numeric" placeholder="e.g. 2" value={page} onChange={(e) => setPage(e.target.value.replace(/\D/g, ""))} />
                </div>
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  Readers see the page or section as printed on the paper. The PDF page is the page of the file that opens; the two can differ.
                </p>
                <Button onClick={insertCitation} className="sm:col-span-2">
                  Insert citation
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Notes and records</h3>
              <ul className="max-h-56 space-y-0.5 overflow-y-auto">
                {noteMatches.map((n) => (
                  <li key={n.id}>
                    <button type="button" className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-accent" onClick={() => props.onInsert(markdownLink(n.title, { type: "note", id: n.id }))}>
                      <KindIcon kind={n.kind} /> {n.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Library</h3>
              <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                {libMatches.slice(0, 10).map((i) => (
                  <li key={i.id}>
                    <button type="button" className="w-full rounded px-2 py-1 text-left text-sm hover:bg-accent" onClick={() => props.onInsert(markdownLink(i.title || "Source", { type: "paper", id: i.id }))}>
                      {i.title || "Untitled source"}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Files</h3>
              <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                {fileMatches.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      className="flex w-full rounded px-2 py-1 text-left text-sm hover:bg-accent"
                      onClick={() => props.onInsert(markdownLink(f.encrypted ? "Encrypted file" : f.filename, { type: "file", id: f.id }))}
                    >
                      <FileName file={f} />
                    </button>
                  </li>
                ))}
                {files.data && !fileMatches.length && <li className="px-2 text-sm text-muted-foreground">No matching files.</li>}
              </ul>
            </section>
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Tasks</h3>
              <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                {taskMatches.map((t) => (
                  <li key={t.id}>
                    <button type="button" className="w-full rounded px-2 py-1 text-left text-sm hover:bg-accent" onClick={() => props.onInsert(markdownLink(t.title, { type: "task", id: t.id }))}>
                      {t.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
